import * as XLSX from "xlsx-js-style";
import { getAllMetricData, MetricGroup } from "../../../dto/MetricGroup";
import { isRowValueEmpty, MetricData, MetricDataRole } from "../../../dto/MetricData";
import { MetricBulkUploadTemplate } from "../../../../excel/templates/metric/MetricBulkUploadTemplate";
import { MetricGroupsAndPeriods, MetricPeriod, MetricPeriodWrapper } from "../../../dto/MetricGroupsAndPeriods";
import { UUID } from "../../../../../core/pojo/id-variable";
import { cloneDeep } from "lodash";
import { CompanyMiniDto } from "../../../../recovery/component/recovery-company/dto/CompanyWithOrgDto";

export class PortfolioAnalysisAllMetricsTemplate {
  /**
   * Exports to excel all the metrics with role sorting (actual/budget).
   *
   * @param metricGroups must have at least one metric -> companyMetric
   * @param orgName will be used for the Excel file naming.
   * @param role to save either actual or budget metric data.
   * @param companies to map the name and uuid, name is used for the sheet naming.
   * @return true if the Excel was successfully created. False if no company has metric data.
   */
  export(metricGroups: MetricGroup[], orgName: string, role: MetricDataRole, companies: CompanyMiniDto[]): boolean {
    const book = XLSX.utils.book_new();
    const companiesToIterate = this.getCompanies(metricGroups, role);
    if (!companiesToIterate.length) {
      return false;
    }
    companiesToIterate.forEach((company) => {
      const groupsAndPeriods = this.getMetricGroupsAndPeriods(cloneDeep(metricGroups), company, role);
      const periods = (role === MetricDataRole.ACTUAL ? groupsAndPeriods.periods.actual : groupsAndPeriods.periods.budget)
        .map((period) => period.period);
      const uniquePeriods = [...new Set(periods)];
      const sheet = new MetricBulkUploadTemplate(uniquePeriods).getSheet(groupsAndPeriods);
      const companyDetails = companies.filter((companyMini) => companyMini.uuid === company)[0];
      XLSX.utils.book_append_sheet(book, sheet, companyDetails.name);
    });
    XLSX.writeFile(book, `${orgName} - Metric Groups (${role}).xlsx`);
    return true;
  }

  /**
   * Provides the company UUIDs, which have non-empty metric data, and metric data is same as param role.
   *
   * @param metricGroups to be iterated to find metrics -> company metrics -> companyUUID.
   * @param role for filtering metric data (actual/budget).
   * @return unique company uuids, who have metric data.
   */
  private getCompanies(metricGroups: MetricGroup[], role: MetricDataRole): UUID[] {
    // no groups
    if (!metricGroups || !metricGroups.length) {
      return [];
    }
    // no metrics / companies
    if (!metricGroups[0].metrics.length || !metricGroups[0].metrics[0].companyMetrics.length) {
      return [];
    }
    const companyMetrics = metricGroups.flatMap((metricGroup) =>
      metricGroup.metrics.flatMap((metric) =>
        metric.companyMetrics));
    const nonEmptyCompanies = companyMetrics
      .filter((companyMetric) => companyMetric.metricData
        .some((metricData) => !isRowValueEmpty(metricData.rowValue) && metricData.role === role))
      .map((companyMetric) => companyMetric.companyUUID);
    const withoutDuplicatesCompanies = [...new Set(nonEmptyCompanies)];
    return withoutDuplicatesCompanies;
  }

  private getMetricGroupsAndPeriods(metricGroupsWithManyCompanies: MetricGroup[], company: UUID, role: MetricDataRole): MetricGroupsAndPeriods {
    const metricGroups = this.getMetricGroupsWithOneCompany(metricGroupsWithManyCompanies, company, role);
    return MetricGroupsAndPeriods.deserializeMetricGroupsAndPeriods({
      periods: this.getPeriods(metricGroups),
      metricGroups,
    } as MetricGroupsAndPeriods);
  }

  /**
   * Returns the whole metric structure, but limiting the companyMetrics from all org companies to the selected company.
   *
   * @param metricGroups with all companies.
   * @param company is selected to filter the company metrics.
   * @param role final filter for metric data.
   * @return metricGroups with one company.
   */
  private getMetricGroupsWithOneCompany(metricGroups: MetricGroup[], company: UUID, role: MetricDataRole): MetricGroup[] {
    return metricGroups.map((metricGroup) => {
      metricGroup.metrics.forEach((metric) => {
        // company metrics only from param company.
        metric.companyMetrics = metric.companyMetrics
          .filter((companyMetric) => companyMetric.companyUUID === company);
        // metric data role only from param role.
        metric.companyMetrics.forEach((companyMetric) =>
          companyMetric.metricData = companyMetric.metricData.filter((md) => md.role === role));
      });
      return metricGroup;
    });
  }

  private getPeriods(metricGroups: MetricGroup[]): MetricPeriodWrapper {
    const allMetricData = getAllMetricData(metricGroups);
    // get periods for specific role, isRequested is always false.
    const getPeriod = (metricData: MetricData[], role: MetricDataRole): MetricPeriod[] => {
      return [...new Set(metricData
        .filter((md) => md.role === role)
        .map((md) => {
          return {
            period: md.period,
            isRequested: false,
          } as MetricPeriod;
        }))];
    };

    return {
      actual: getPeriod(allMetricData, MetricDataRole.ACTUAL),
      budget: getPeriod(allMetricData, MetricDataRole.BUDGET),
    };
  }
}
